import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";

function AddLevel() {
  // URL parameter handling
  const [searchParams] = useSearchParams();
  const levelId = searchParams.get("id");

  const navigate = useNavigate();

  // State variables
  const [levelName, setLevelName] = useState("");
  const [levelPosition, setLevelPosition] = useState("");
  const [huntCount, setHuntCount] = useState("");
  const [levelsData, setLevelsData] = useState([]);
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [titleError, setTitleError] = useState("");

  // Fetch categories on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch data for editing if levelId is provided
  useEffect(() => {
    if (levelId) {
      fetchData(levelId);
    }
  }, [levelId]);

  const fetchData = async (levelId) => {
    try {
      const response = await axios({
        method: "POST",
        url: process.env.REACT_APP_APIURL,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify({
          environment: "live",
          apikey: process.env.REACT_APP_APIKEY,
          secret: process.env.REACT_APP_SECRET,
          method: "getAllLevels",
          module: "user",
          level_id: levelId
        }),
      });

      const data = response.data;
      if (data.statusCode === 200) {
        setLevelsData(data.result);
        if (levelId) {
          // Pre-fill the form fields if editing a level
          const level = data.result.find(level => level._id === levelId);
          if (level) {
            setLevelName(level.level_name);
            setLevelPosition(level.level_position);
            setHuntCount(level.hunt_count);
          }
        }
      } else {
        console.error("Failed to fetch levels:", data);
        setLevelsData([]); // Clear data if fetching fails
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLevelsData([]); // Clear data if there's an error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsFormProcessing(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (!validateForm()) {
        setIsFormProcessing(false);
        return;
      }

      const response = await axios({
        method: "POST",
        url: process.env.REACT_APP_APIURL,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify({
          environment: "live",
          apikey: process.env.REACT_APP_APIKEY,
          secret: process.env.REACT_APP_SECRET,
          method: levelId ? "updateLevel" : "createLevel", // Use the correct method for add/update
          module: "user",
          level_id: levelId,
          level_name: levelName,
          level_position: levelPosition,
          hunt_count: huntCount
        }),
      });

      if (response.data.status === "success") {
        setSuccessMessage(levelId ? "Level updated successfully!" : "Level added successfully!");
        setTimeout(() => navigate("/levels"), 2000);
      } else {
        setErrorMessage("Failed to save level");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage("Failed to process request");
    } finally {
      setIsFormProcessing(false);
    }
  };

  const validateForm = () => {
    // Simple validation
    if (!levelName || !levelPosition || !huntCount) {
      setTitleError("All fields are required.");
      return false;
    }
    setTitleError(""); // Clear error if form is valid
    return true;
  };

  return (
    <div className="wrapper">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0 txt_lft">{levelId ? "Edit Level" : "Add Level"}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <form className="form-horizontal" onSubmit={handleSubmit}>
                      <div className="card-body">
                        {/* Level Name Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Level Name<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="text"
                              className="form-control"
                              value={levelName}
                              onChange={(e) => setLevelName(e.target.value)}
                            />
                            {titleError && (
                              <small className="text-danger">{titleError}</small>
                            )}
                          </div>
                        </div>

                        {/* Level Position Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Level Position<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="number"
                              className="form-control"
                              value={levelPosition}
                              onChange={(e) => setLevelPosition(e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Hunt Count Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Hunt Count<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="number"
                              className="form-control"
                              value={huntCount}
                              onChange={(e) => setHuntCount(e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Error/Success Messages */}
                        {errorMessage && (
                          <div className="alert alert-danger" role="alert">
                            {errorMessage}
                          </div>
                        )}
                        {successMessage && (
                          <div className="alert alert-success" role="alert">
                            {successMessage}
                          </div>
                        )}
                      </div>

                      {/* Form Submit */}
                      <div className="card-footer text-center">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isFormProcessing}
                        >
                          {isFormProcessing
                            ? "Processing..."
                            : levelId
                              ? "Update"
                              : "Add"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default AddLevel;
